import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, SlidersHorizontal } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SectionHeader from "../components/SectionHeader";
import Reveal from "../components/Reveal";
import VideoFrame from "../components/VideoFrame";
import RequestForm from "../components/RequestForm";
import { useVideos, slug, distribute, CONTACT, BRAND_FILM } from "../data/site";

const GROUPS = [
  { key: "capability", field: "capability", label: "Capability" },
  { key: "who", field: "buyerSituation", label: "Who it's for" },
  { key: "industry", field: "industry", label: "Industry" },
  { key: "format", field: "format", label: "Format" },
];

const PAGE_SIZE = 16;

export default function Work() {
  const videos = useVideos();
  const [params, setParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => setVisible(PAGE_SIZE), [params]);

  const selected = useMemo(() => {
    const s = {};
    GROUPS.forEach((g) => {
      s[g.key] = (params.get(g.key) || "").split("||").filter(Boolean);
    });
    return s;
  }, [params]);

  const options = useMemo(() => {
    const o = {};
    GROUPS.forEach((g) => {
      o[g.key] = [...new Set(videos.map((v) => v[g.field]))];
    });
    return o;
  }, [videos]);

  const filtered = useMemo(
    () =>
      distribute(
        videos.filter((v) =>
          GROUPS.every((g) => !selected[g.key].length || selected[g.key].includes(v[g.field]))
        )
      ),
    [videos, selected]
  );

  const activeCount = Object.values(selected).flat().length;
  const grid = activeCount ? filtered : [BRAND_FILM, ...filtered];

  const closest = useMemo(() => {
    if (filtered.length >= 3 || !activeCount) return [];
    const ids = new Set(filtered.map((v) => v.id));
    return videos
      .filter((v) => !ids.has(v.id) && GROUPS.some((g) => selected[g.key].includes(v[g.field])))
      .slice(0, 3);
  }, [videos, filtered, selected, activeCount]);

  const toggle = (key, value) => {
    const next = new URLSearchParams(params);
    const cur = selected[key];
    const arr = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    if (arr.length) next.set(key, arr.join("||"));
    else next.delete(key);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const few = filtered.length > 0 && filtered.length < 3;

  return (
    <main data-testid="work-page" className="bg-black min-h-screen">
      <Nav />
      <div className="pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="container-x">
          <SectionHeader
            eyebrow="The full collection"
            eyebrowTestId="collection-eyebrow"
            title="The full collection."
            support="Every industry. Every format. And still just a sample."
          />
        </div>

        <div className="sticky top-16 md:top-[72px] z-30 bg-black/85 backdrop-blur-xl border-b border-white/10">
          <div className="container-x py-3">
            <div className="flex items-center justify-between gap-3">
              <button
                data-testid="filters-toggle-btn"
                onClick={() => setFiltersOpen((o) => !o)}
                className="btn-secondary !min-h-[40px] !px-5 !text-xs"
                aria-expanded={filtersOpen}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Filters
                {activeCount > 0 && (
                  <span
                    data-testid="filters-active-badge"
                    className="ml-1 w-5 h-5 rounded-full bg-[#C8F031] text-black text-[10px] font-extrabold flex items-center justify-center"
                  >
                    {activeCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-4">
                <p data-testid="results-count" className="text-xs font-heading font-semibold text-white/40">
                  Showing <span className="text-[#C8F031]">{filtered.length}</span> of {videos.length}
                </p>
                {activeCount > 0 && (
                  <button
                    data-testid="clear-filters-btn"
                    onClick={clearAll}
                    className="flex items-center gap-1.5 text-xs font-heading font-bold text-white/60 hover:text-[#C8F031] transition-colors min-h-[36px]"
                  >
                    <X className="w-3.5 h-3.5" /> Clear all
                  </button>
                )}
              </div>
            </div>
            <AnimatePresence initial={false}>
              {filtersOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pt-5 pb-2 space-y-3 lg:space-y-5" data-testid="filters-panel">
                    {GROUPS.map((g) => (
                      <div key={g.key} className="flex lg:flex-row flex-col lg:items-center gap-2 lg:gap-4">
                        <span className="lg:w-28 shrink-0 text-[10px] font-heading font-bold uppercase tracking-[0.22em] text-white/35">
                          {g.label}
                        </span>
                        <div className="flex gap-2 overflow-x-auto lg:overflow-visible no-scrollbar lg:flex-wrap pb-1 lg:pb-0">
                          {options[g.key]?.map((opt) => {
                            const on = selected[g.key].includes(opt);
                            return (
                              <button
                                key={opt}
                                data-testid={`filter-${g.key}-${slug(opt)}`}
                                onClick={() => toggle(g.key, opt)}
                                className={`shrink-0 rounded-full px-4 py-2 text-xs font-heading font-semibold border transition-colors duration-200 min-h-[36px] ${
                                  on
                                    ? "bg-[#C8F031] text-black border-[#C8F031]"
                                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="container-x pt-10 md:pt-14">
          {(few || filtered.length === 0) && <RequestCard empty={filtered.length === 0} />}
          {closest.length > 0 && (
            <div className="mb-14" data-testid="closest-matches">
              <p className="text-[10px] font-heading font-bold uppercase tracking-[0.25em] text-white/35">
                The closest we've made
              </p>
              <div className="work-grid grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                {closest.map((v) => (
                  <VideoFrame key={v.id} video={v} />
                ))}
              </div>
            </div>
          )}
          {filtered.length > 0 && (
            <>
              <div className="work-grid grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="collection-grid">
                {grid.slice(0, visible).map((v) => (
                  <VideoFrame key={v.id} video={v} />
                ))}
              </div>
              {visible < grid.length && (
                <div className="mt-14 text-center">
                  <button
                    data-testid="load-more-btn"
                    onClick={() => setVisible((c) => c + PAGE_SIZE)}
                    className="btn-secondary"
                  >
                    Load more
                  </button>
                </div>
              )}
              {!few && (
                <Reveal className="mt-16 md:mt-24">
                  <RequestCard />
                </Reveal>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}

const RequestCard = ({ empty = false }) => (
  <div
    data-testid={empty ? "request-card-empty" : "request-card"}
    className="rounded-3xl border border-white/15 bg-[#111] p-6 md:p-12 mb-12 last:mb-0"
  >
    <div className="max-w-2xl mx-auto text-center">
      <p className="eyebrow">No dead ends here</p>
      <h3 className="mt-4 font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-white">
        Can't find your exact use case?
      </h3>
      <p className="mt-3 text-sm text-white/50 leading-relaxed">
        Tell us what you're trying to make. We've probably made something close.
      </p>
      <div className="mt-8 text-left">
        <RequestForm source="work-page" testid="work-request" />
      </div>
      <a
        data-testid="work-request-free-edit-link"
        href={CONTACT.freeEditLink}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center mt-6 font-heading text-sm font-bold text-[#C8F031] hover:text-white transition-colors min-h-[44px]"
      >
        Or skip the form — get a free 30-second edit on WhatsApp →
      </a>
    </div>
  </div>
);
